import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Headset, UserCheck, X } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Input, Select, Textarea, useDescarteProtegido, useHayCambios } from "../../components/ui";
import { SocioAutocomplete } from "../../components/SocioAutocomplete";
import {
  fetchContratos,
  type ContratoOperativo,
  type SocioRead,
} from "../socios/api";
import {
  CANAL_LABELS,
  TIPO_LABELS,
  crearAtencion,
  type Atencion,
  type AtencionCreateInput,
  type CanalRecepcion,
  type TipoAtencion,
} from "./api";
import { ComprobanteAtencion } from "./ComprobanteAtencion";
import { fechaDMA, fechaLocalISO, sumarDiasHabiles } from "../../lib/fechaLocal";

const TIPOS: TipoAtencion[] = ["Solicitud", "Consulta", "Emergencia", "Reclamo"];
const CANALES: CanalRecepcion[] = [
  "presencial",
  "telefono",
  "email",
  "whatsapp",
  "carta",
  "libro",
];

const FORM_ID = "crear-atencion-form";

/** Plazo que verá el usuario, antes de registrar (el exacto lo da el servidor). */
function plazoEstimado(tipo: TipoAtencion): string {
  if (tipo === "Emergencia") return "Plazo de respuesta: el mismo día.";
  const aprox = fechaLocalISO(sumarDiasHabiles(new Date(), 20));
  return `Plazo de respuesta: 20 días hábiles, cerca del ${fechaDMA(aprox)} (el comprobante descuenta los feriados).`;
}

export function CrearAtencionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** Se llama al registrar; el modal sigue abierto mostrando el comprobante. */
  onCreated: (a: Atencion) => void;
}): JSX.Element {
  const toast = useToast();
  const [tipo, setTipo] = useState<TipoAtencion>("Solicitud");
  const [canal, setCanal] = useState<CanalRecepcion>("presencial");
  const [usuarioNombre, setUsuarioNombre] = useState("");
  const [usuarioRut, setUsuarioRut] = useState("");
  const [usuarioTelefono, setUsuarioTelefono] = useState("");
  const [usuarioEmail, setUsuarioEmail] = useState("");
  const [contratoId, setContratoId] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  // Socio vinculado (autocomplete). null = registro manual / no socio.
  const [socio, setSocio] = useState<SocioRead | null>(null);
  // Atención recién registrada: el modal pasa a mostrar su comprobante.
  const [creada, setCreada] = useState<Atencion | null>(null);

  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: fetchContratos });
  const contratosDelSocio = useMemo<ContratoOperativo[]>(
    () =>
      socio == null
        ? []
        : (contratosQ.data ?? []).filter((c) => c.socio_id === socio.id),
    [contratosQ.data, socio],
  );

  function vincularSocio(s: SocioRead): void {
    setSocio(s);
    setUsuarioNombre(s.nombre);
    setUsuarioRut(s.rut ?? "");
    setUsuarioTelefono(s.telefono ?? "");
    setUsuarioEmail(s.correo ?? "");
    // Auto-selecciona el contrato si el socio tiene exactamente uno.
    const suyos = (contratosQ.data ?? []).filter((c) => c.socio_id === s.id);
    setContratoId(suyos.length === 1 ? String(suyos[0]!.id) : "");
  }

  function desvincularSocio(): void {
    setSocio(null);
    setUsuarioNombre("");
    setUsuarioRut("");
    setUsuarioTelefono("");
    setUsuarioEmail("");
    setContratoId("");
  }

  const mut = useMutation({
    mutationFn: (input: AtencionCreateInput) => crearAtencion(input),
    onSuccess: (a) => {
      setCreada(a);
      onCreated(a);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo registrar";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (descripcion.trim().length < 3) {
      toast.error("Describe el motivo en al menos 3 caracteres.");
      return;
    }
    if (usuarioNombre.trim().length === 0) {
      toast.error("Falta el nombre de quien consulta.");
      return;
    }
    mut.mutate({
      tipo,
      canal_recepcion: canal,
      usuario_nombre: usuarioNombre.trim(),
      usuario_rut: usuarioRut.trim() || null,
      usuario_telefono: usuarioTelefono.trim() || null,
      usuario_email: usuarioEmail.trim() || null,
      socio_id: socio?.id ?? null,
      contrato_id: contratoId.trim() ? Number(contratoId.trim()) : null,
      descripcion: descripcion.trim(),
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      tipo, canal, usuarioNombre, usuarioRut, usuarioTelefono, usuarioEmail,
      contratoId, descripcion, observaciones, socio?.id ?? null,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios && !creada,
    onClose,
    mut.isPending,
  );

  if (creada) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Atención registrada"
        icon={Headset}
        size="lg"
      >
        <ComprobanteAtencion atencion={creada} onListo={onClose} />
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva atención"
      icon={Headset}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} disabled={mut.isPending}>
            {mut.isPending ? "Registrando…" : "Registrar atención"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Tipo de atención"
            hint={plazoEstimado(tipo)}
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoAtencion)}
          >
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {TIPO_LABELS[t]}
              </option>
            ))}
          </Select>
          <Select
            label="Canal de recepción"
            value={canal}
            onChange={(e) => setCanal(e.target.value as CanalRecepcion)}
          >
            {CANALES.map((c) => (
              <option key={c} value={c}>
                {CANAL_LABELS[c]}
              </option>
            ))}
          </Select>
        </div>

        {/* ── Solicitante: vincular socio del padrón o registrar manual ── */}
        {socio ? (
          <div className="space-y-3 rounded-md border border-brand-200 bg-brand-50/50 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-brand-700" strokeWidth={1.5} />
                <div>
                  <div className="text-sm font-medium text-neutral-800">
                    {socio.nombre}
                  </div>
                  <div className="text-xs text-neutral-600">
                    {socio.rut}
                    {socio.numero_socio != null ? ` · N° ${socio.numero_socio}` : ""}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={desvincularSocio}
                className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-rose-700"
              >
                <X className="h-3.5 w-3.5" /> Quitar
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Select
                label="Contrato"
                value={contratoId}
                onChange={(e) => setContratoId(e.target.value)}
              >
                <option value="">— sin contrato —</option>
                {contratosDelSocio.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.arranque_codigo ?? `Arr. ${c.arranque_id}`}
                    {c.arranque_direccion ? ` · ${c.arranque_direccion}` : ""}
                  </option>
                ))}
              </Select>
              <Input
                label="Teléfono"
                value={usuarioTelefono}
                onChange={(e) => setUsuarioTelefono(e.target.value)}
                placeholder="+56 9 1234 5678"
              />
              <Input
                label="Correo"
                type="email"
                value={usuarioEmail}
                onChange={(e) => setUsuarioEmail(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3 rounded-md border border-neutral-200 p-3">
            <SocioAutocomplete onSelect={vincularSocio} />
            <p className="text-xs text-neutral-600">
              ¿No es socio del padrón? Completa los datos manualmente abajo.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Nombre del usuario"
                value={usuarioNombre}
                onChange={(e) => setUsuarioNombre(e.target.value)}
                required
                placeholder="Andrés Toledo"
              />
              <Input
                label="RUT (opcional)"
                value={usuarioRut}
                onChange={(e) => setUsuarioRut(e.target.value)}
                placeholder="12.345.678-9"
              />
              <Input
                label="Teléfono (opcional)"
                value={usuarioTelefono}
                onChange={(e) => setUsuarioTelefono(e.target.value)}
                placeholder="+56 9 1234 5678"
              />
              <Input
                label="Correo (opcional)"
                type="email"
                value={usuarioEmail}
                onChange={(e) => setUsuarioEmail(e.target.value)}
              />
            </div>
          </div>
        )}

        <Textarea
          label="Descripción breve"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          required
          rows={3}
          placeholder="Visita a domicilio para revisión de medidor"
        />
        <Textarea
          label="Observaciones (opcional)"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}
